import { GeneratorResponseDTO } from '@/service/generator';
import { HummingAudioType, UpdateFileTypeEnum, UploadForAcrResponseDTO } from '@/service/generatorUpload';
import { AudioDetailDto } from '@/service/interface';
import { AudioDto } from '@/service/type';
import { Control, UseFormRegister, UseFormReturn } from 'react-hook-form';
import { create } from 'zustand';

export type FormValues = {
  prompt: string;
  model: string;
  quality: string | undefined;
  duration: number;
  assist: string;
  trackName: string;
  folderName: string;
  folderId: number | undefined | null;
  styleFilterId?: number;
  styleFilterIsOpened?: boolean;
  filterWeight?: number;
  filterStrength?: number;
  styleFilters?: StyleFilters[];
};

type StyleFilters = {
  sfId: number;
  type: 'main' | 'secondary';
};

export enum EditorTypeEnum {
  'instrument' = 'instrument',
  'text' = 'text',
}

export enum RecordingStatusEnum {
  'WaitingRecording' = 'WaitingRecording',
  'Recording' = 'Recording',
  'RecordingFinished' = 'RecordingFinished',
  'RecordingWarning' = 'RecordingWarning',
}

export enum R3imagineStatusType {
  'WaitingUpload' = 'waiting upload',
  'UploadLoading' = 'upload loading',
  'WaitingAgree' = 'waiting agree',
  'WaitingGenerate' = 'waiting generate',
}

export enum R3ImageTypeEnum {
  'record' = 'record',
  'select' = 'select',
  'upload' = 'upload',
}

export type FormFieldKeys = keyof FormValues;

export enum GeneratorTypeEnums {
  TEXT = 'TEXT',
  R3IMAGINE = 'R3IMAGINE',
  EDITOR = 'EDITOR',
}

export enum EditorInstrumentEnum {
  AcousticGuitar = 'Acoustic Guitar',
  ElectricGuitar = 'Electric Guitar',
  ElectricPiano = 'Electric Piano',
  FlamencoGuitar = 'Flamenco Guitar',
  GrandPiano = 'Grand Piano',
  Harp = 'Harp',
  Violin = 'Violin',
  Saxophone = 'Saxophone',
  SteelGuitar = 'Steel Guitar',
  Trombone = 'Trombone',
  Trumpet = 'Trumpet',
  Bass = 'Bass',
}

export const defaultGeneratorFormValues: FormValues = {
  prompt: '',
  model: '1.6sf',
  quality: 'mp3',
  duration: 60,
  assist: 'ON',
  trackName: '',
  folderName: '',
  folderId: undefined,
  styleFilterId: undefined,
  filterWeight: 0,
  styleFilterIsOpened: false,
  filterStrength: undefined,
  styleFilters: [],
};

interface RecordFile {
  file: Blob;
  name?: string;
  size?: number;
  status?: string;
  errorMessage: string | null;
  filePath?: string;
  src?: string;
  id?: number;
  duration: number;
  type: UpdateFileTypeEnum;
}

export interface GeneratorStore {
  isGeneratorDrawerShow: boolean;
  setIsGeneratorDrawerShow: (show: boolean) => void;
  r3ImageStatus: R3imagineStatusType;
  setR3ImageStatus: (status: R3imagineStatusType) => void;
  currentRecordFile: RecordFile;
  setCurrentRecordFile: (file: RecordFile) => void;
  generatorType: GeneratorTypeEnums;
  setGeneratorType: (tab: GeneratorTypeEnums) => void;
  generatorActionTrigger: boolean;
  generatorResult: GeneratorResponseDTO;
  setGeneratorResult: (result: GeneratorResponseDTO) => void;
  resetGeneratorResult: () => void;
  formValues: FormValues;
  setFormValues: (values: FormValues) => void;
  register: UseFormRegister<FormValues>;
  generatorForm: UseFormReturn<FormValues, any, undefined> | undefined;
  setGeneratorForm: (form: UseFormReturn<FormValues, any, undefined>) => void;
  setRegister: (register: UseFormRegister<FormValues>) => void;
  control: Control<FormValues> | undefined;
  setControl: (control: Control<FormValues>) => void;
  hummingAudioSelected: null | HummingAudioType;
  setHummingAudioSelected: (audio: null | HummingAudioType) => void;
  uploadFileData: UploadForAcrResponseDTO | undefined;
  setUploadFileData: (fileData: UploadForAcrResponseDTO | undefined) => void;
  editorType: EditorTypeEnum;
  setEditorType: (type: EditorTypeEnum) => void;
  editorInstrument: string;
  setEditorInstrument: (instrument: string) => void;
  editorSelectedInstrument: EditorInstrumentEnum | undefined;
  setEditorSelectedInstrument: (instrument: EditorInstrumentEnum) => void;
  recordingStatus: RecordingStatusEnum;
  setRecordingStatus: (recordingStatus: RecordingStatusEnum) => void;
  recordingTime: number;
  setRecordingTime: (recordingTime: number) => void;
  trackName: string;
  setTrackName: (trackName: string) => void;
  folderName: string;
  setFolderName: (folderName: string) => void;
  audioInfo: AudioDto | AudioDetailDto;
  setAudioInfo: (audioInfo: AudioDto | AudioDetailDto) => void;
  audioUploadingStatus: 'checking' | 'updating' | 'success' | 'error';
  setAudioUploadingStatus: (audioUploadingStatus: 'checking' | 'updating' | 'success' | 'error') => void;
  audioPlayerLoading: boolean;
  setAudioPlayerLoading: (audioPlayerLoading: boolean) => void;
  isCountdown: boolean;
  setIsCountdown: (isCountdown: boolean) => void;
  tracksCount: number;
  setTracksCount: (value: number | ((prev: number) => number)) => void;
  r3ImageType: R3ImageTypeEnum;
  setR3ImageType: (type: R3ImageTypeEnum) => void;
}

const useGeneratorStore = create<GeneratorStore>((set, get) => ({
  generatorType: GeneratorTypeEnums.TEXT,
  setGeneratorType: (tab: GeneratorTypeEnums) => {
    set({ generatorType: tab });
  },
  generatorActionTrigger: false,
  generatorResult: {
    data: [],
    creditBalance: 0,
  },
  setGeneratorResult: (result: GeneratorResponseDTO) => {
    set({ generatorResult: result });
    set({ generatorActionTrigger: true });
  },
  resetGeneratorResult: () => {
    set({ generatorActionTrigger: false });
  },
  formValues: defaultGeneratorFormValues,
  currentRecordFile: {
    file: new Blob(),
    name: '',
    size: 0,
    status: '',
    errorMessage: null,
    filePath: '',
    src: '',
    id: 0,
    duration: 0,
    type: UpdateFileTypeEnum.record,
  },
  setCurrentRecordFile: (file: RecordFile) => {
    set({ currentRecordFile: file });
  },
  setFormValues: (values: FormValues) => {
    set({ formValues: values });
  },
  // eslint-disable-next-line prettier/prettier
  register: (() => {}) as unknown as UseFormRegister<FormValues>,
  setRegister: (register: UseFormRegister<FormValues>) => {
    set({ register: register });
  },
  isGeneratorDrawerShow: false,
  setIsGeneratorDrawerShow: (show: boolean) => {
    set({ isGeneratorDrawerShow: show });
  },
  control: undefined,
  setControl: (control: Control<FormValues>) => {
    set({ control: control });
  },
  hummingAudioSelected: null,
  setHummingAudioSelected: (audio: null | HummingAudioType) => {
    set({ hummingAudioSelected: audio });
  },
  r3ImageStatus: R3imagineStatusType.WaitingUpload,
  setR3ImageStatus: (status: R3imagineStatusType) => {
    set({ r3ImageStatus: status });
  },
  r3ImageType: R3ImageTypeEnum.select,
  setR3ImageType: (type: R3ImageTypeEnum) => {
    set({ r3ImageType: type });
  },
  editorType: EditorTypeEnum.text,
  setEditorType: (type: EditorTypeEnum) => {
    set({ editorType: type });
  },
  editorInstrument: '',
  setEditorInstrument: (instrument: string) => {
    set({ editorInstrument: instrument });
  },
  editorSelectedInstrument: undefined,
  setEditorSelectedInstrument: (instrument: EditorInstrumentEnum) => {
    set({ editorSelectedInstrument: instrument });
  },
  recordingStatus: RecordingStatusEnum.WaitingRecording,
  setRecordingStatus: (recordingStatus: RecordingStatusEnum) => {
    set({ recordingStatus: recordingStatus });
  },
  recordingTime: 0,
  setRecordingTime: (recordingTime: number) => {
    set({ recordingTime: recordingTime });
  },
  trackName: '',
  setTrackName: (trackName: string) => {
    set({ trackName: trackName });
  },
  folderName: '',
  setFolderName: (folderName: string) => {
    set({ folderName: folderName });
  },
  audioInfo: {} as AudioDto | AudioDetailDto,
  setAudioInfo: (audioInfo: AudioDto | AudioDetailDto) => {
    set({ audioInfo: audioInfo });
  },
  generatorForm: undefined,
  setGeneratorForm: (form: UseFormReturn<FormValues, any, undefined>) => {
    set({
      generatorForm: form,
    });
  },
  audioUploadingStatus: 'success',
  setAudioUploadingStatus: (audioUploadingStatus: 'checking' | 'updating' | 'success' | 'error') => {
    set({ audioUploadingStatus: audioUploadingStatus });
  },
  uploadFileData: undefined,
  setUploadFileData: (fileData: UploadForAcrResponseDTO | undefined) => {
    set({ uploadFileData: fileData });
  },
  audioPlayerLoading: false,
  setAudioPlayerLoading: (audioPlayerLoading: boolean) => {
    set({ audioPlayerLoading });
  },
  isCountdown: false,
  setIsCountdown: (isCountdown: boolean) => {
    set({ isCountdown });
  },
  tracksCount: 1,
  setTracksCount: (value: number | ((prev: number) => number)) => {
    set((state) => ({
      tracksCount: typeof value === 'number' ? value : value(state.tracksCount),
    }));
  },
}));

export default useGeneratorStore;
